import { Tab } from '@headlessui/react';
import { useRouter } from 'next/dist/client/router';
import { useState } from 'react';
import { isSkillup, isWhereWeGo } from '../../../lib/config/project/project';
import { Filter, SingleFilter, Sort } from '../../../lib/types';
import Adjustments from '../../icons/Adjustments';
import ChevronUp from '../../icons/ChevronUp';
import FilterIcon from '../../icons/Filters';
import MobileFilter from '../MobileFilter';
import MobileSort from '../MobileSort';

interface MobileFiltersAndSortProps {
  filters: Filter[];
  sortOptions: Sort[];
  selectedSort: Sort;
  potentialNumberOfResults: number;
  updatePotentialFilterForParam: (filter: SingleFilter) => void;
  clearAllFilters: () => void;
  applySort: (sort: Sort) => void;
  seeAllResults: () => void;
  totalNumberOfResults: number;
  isCareers?: boolean;
}

const MobileFiltersAndSort: React.FC<MobileFiltersAndSortProps> = ({
  filters,
  sortOptions,
  selectedSort,
  potentialNumberOfResults,
  updatePotentialFilterForParam,
  clearAllFilters,
  applySort,
  seeAllResults,
  totalNumberOfResults,
  isCareers,
}) => {
  const router = useRouter();
  // Whether or not the mobile filters are open
  const [isOpen, setIsOpen] = useState(false);
  // Whether or not a tab group is active
  const [active, setActive] = useState(false);
  // This stores data of which filters are currently toggled on by user

  const getActiveFilters = (paramLabel: string): string => {
    const { query } = router;
    const activeFilters = query[paramLabel]; // the active values for that particular filter
    if (!activeFilters) return '';
    const activeFiltersArray = Array.isArray(activeFilters)
      ? (activeFilters as string[])
      : activeFilters?.split('_'); // Get an array from the url values

    // If the array is only two long, we will just join those
    // two values with a comma (,) and display them both
    if (activeFiltersArray.length <= 2) {
      return `(${activeFiltersArray.join(', ')})`;
    }
    // If the array is longer than 2, we will
    // show the first two values and then a "+ X more" message
    // where X is how many additional values are selected
    const plusMore = activeFiltersArray.length - 2;
    return `(${activeFiltersArray.slice(0, 2).join(', ')} + ${plusMore} more)`;
  };

  const bgClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'bg-primary'
      : 'bg-careerPageHeading';

  const borderClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'border-primary'
      : 'border-careerPageHeading';

  const lightBgClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'bg-light-primary'
      : 'bg-careerPageHeading/50';

  const textClass =
    isSkillup || (isWhereWeGo && !isCareers)
      ? 'text-primary'
      : 'text-careerPageHeading';

  return (
    <>
      {Boolean(isWhereWeGo && isCareers) && (
        <div className="mt-4">
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center justify-between w-full h-16 p-4 text-base text-white border-2 border-solid border-careerPageHighlight lg:hidden font-base"
          >
            Filters
            <FilterIcon className="w-6 h-6 fill-white" />
          </button>
        </div>
      )}
      <section className="fixed bottom-0 left-0 right-0 z-50 flex items-center w-full bg-transparent lg:hidden">
        {Boolean(!isOpen && !isCareers) && (
          <button
            onClick={() => {
              setIsOpen(true);
            }}
            className={`py-4 px-6 rounded-full ${bgClass} flex disabled:cursor-not-allowed w-auto mx-auto m-6 font-base self-stretch font-extrabold outline-none`}
          >
            <span className="text-xl text-white">Filters</span>
            &nbsp; &nbsp;
            <Adjustments className="w-6 h-6 rotate-90 fill-white" />
          </button>
        )}
        {isOpen && (
          <div className="fixed inset-0 bg-white ">
            <div className="relative h-screen p-4 overflow-x-hidden overflow-y-scroll">
              {!active && (
                <>
                  <h4 className="py-2 my-4 text-lg font-semibold text-center text-gray1 font-base ">
                    Filter + Sort
                  </h4>
                  <span
                    className="absolute text-sm font-base text-gray1 right-4 top-4"
                    onClick={() => setIsOpen(false)}
                  >
                    Done
                  </span>
                </>
              )}
              <div className="h-full pb-10 -mx-6">
                <div className="relative h-full">
                  <Tab.Group>
                    <Tab.List>
                      <p
                        className={`p-4 font-bold uppercase ${textClass} ${lightBgClass}`}
                      >
                        Filters
                      </p>
                      <div className="py-4">
                        {filters.map((filter) => {
                          return (
                            <Tab key={filter.label} className="flex w-full">
                              <button
                                onClick={() => setActive(true)}
                                className="flex items-center justify-between w-full px-6 py-2 text-left"
                              >
                                <span className="flex items-center">
                                  <p className="font-bold text-gray2 font-base">
                                    {filter.label}
                                  </p>
                                  <p className="ml-2 text-xs capitalize text-gray8 font-base shrink">
                                    {getActiveFilters(filter.paramLabel)}
                                  </p>
                                </span>
                                <ChevronUp className="w-5 h-5 rotate-90 stroke-gray8 shrink-0" />{' '}
                              </button>
                            </Tab>
                          );
                        })}
                      </div>
                      <div className="h-full py-4">
                        <p
                          className={`p-4 font-bold uppercase ${textClass} ${lightBgClass}`}
                        >
                          Sort By
                        </p>
                        <MobileSort
                          selectedSort={selectedSort}
                          applySort={applySort}
                          sortOptions={sortOptions}
                          isCareers={isCareers}
                        />
                        <div className="grid grid-cols-1 gap-4 p-4">
                          <p className="text-center">
                            {totalNumberOfResults} Results
                          </p>
                          <button
                            className={`w-full p-4 text-lg font-bold border-solid rounded-md ${textClass} border-1 ${borderClass} font-base`}
                            onClick={clearAllFilters}
                            tabIndex={1}
                          >
                            Clear All Filters
                          </button>
                          <button
                            className={`w-full p-4 text-lg font-bold text-white rounded-lg ${bgClass} font-base`}
                            onClick={() => {
                              seeAllResults();
                              setIsOpen(false);
                            }}
                            tabIndex={1}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </Tab.List>
                    {active && (
                      <div>
                        <Tab.Panels className="absolute inset-0 h-screen p-4 list-none bg-white">
                          {filters.map((filter) => {
                            return (
                              <Tab.Panel
                                key={`panel-${filter.paramLabel}`}
                                className="h-full"
                              >
                                <MobileFilter
                                  filterOptions={filter.options || []}
                                  paramLabel={filter.paramLabel}
                                  label={filter.label}
                                  setActive={setActive}
                                  seeAllResults={seeAllResults}
                                  potentialNumberOfResults={
                                    potentialNumberOfResults
                                  }
                                  updatePotentialFilterForParam={
                                    updatePotentialFilterForParam
                                  }
                                  isCareers={isCareers}
                                />
                              </Tab.Panel>
                            );
                          })}
                        </Tab.Panels>
                      </div>
                    )}
                  </Tab.Group>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default MobileFiltersAndSort;
